import React, { useState, useEffect } from 'react';
import { 
  FaWhatsapp, 
  FaTelegram, 
  FaLink, 
  FaUsers, 
  FaUserPlus, 
  FaCoins, 
  FaMoneyBillWave,
  FaChartLine,
  FaCopy,
  FaShare,
  FaCheck,
  FaTwitter,
  FaFacebook,
  FaGift,
  FaRocket,
  FaNetworkWired,
  FaHandshake,
  FaBullhorn,
  FaUserFriends,
  FaLongArrowAltRight,
  FaTrophy,
  FaChevronDown,
  FaChevronUp,
  FaArrowRight,
  FaInfoCircle,
  FaBuilding,
  FaIndustry,
  FaTrendingUp,
  FaGlobe,
  FaShieldAlt
} from 'react-icons/fa';
import { MdBusiness, MdAttachMoney, MdBarChart } from 'react-icons/md';
import { IoBusiness, IoTrendingUp } from 'react-icons/io5';
import BottomNavigation from './BottomNavigation';
import { useAuth } from '../security/security/AuthContext';
import axios from 'axios';
import Loading from './auth/loaders/Loading';
import WhatsAppFloat from './auth/WhatsAppFloat';

const AffiliateProgram = () => {
  const { userDetails, token, updateUserDetails } = useAuth();
  const [inviteLink, setInviteLink] = useState('');
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [expandedSection, setExpandedSection] = useState(null);
  const [referralHistory, setReferralHistory] = useState([]);
  const [copyButtonText, setCopyButtonText] = useState('Copy');
  const [showShareOptions, setShowShareOptions] = useState(false);
  
  // Application process states
  const [agentDetails, setAgentDetails] = useState(null);
  const [isApplying, setIsApplying] = useState(false);
  const [onboardingStep, setOnboardingStep] = useState(0);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [currentTipIndex, setCurrentTipIndex] = useState(0);
  const [showTip, setShowTip] = useState(false);

  const onboardingSteps = [
    {
      title: "Welcome to Share Investment Partners",
      description: "Join our exclusive community of investment partners and earn commissions when you share opportunities with others.",
      icon: <FaHandshake size={48} style={{color: "var(--primary)"}} />
    },
    {
      title: "Earn Up to 10% Commission",
      description: "Get rewarded with up to 10% commission on share investments made by users you refer. Build wealth together!",
      icon: <MdAttachMoney size={48} style={{color: "var(--primary)"}} />
    },
    {
      title: "Multi-Level Rewards",
      description: "Earn from your direct partners and their network up to 3 levels deep. Create a sustainable passive income stream.",
      icon: <FaNetworkWired size={48} style={{color: "var(--primary)"}} />
    },
    {
      title: "Easy Sharing Tools",
      description: "Share your unique invitation code via WhatsApp, Telegram, Twitter or Facebook. We provide everything you need to succeed.",
      icon: <FaShare size={48} style={{color: "var(--primary)"}} />
    }
  ];


  const [tips] = useState([
    "💡 Share during market hours when investors are most active!",
    "🎯 Personalize your investment invitation messages for better engagement",
    "📱 Follow up with referred users to help them make their first share purchase",
    "🌟 Share success stories about company share investments",
    "💪 Target investment enthusiasts and financial planners for higher commissions",
    "🤝 Build lasting relationships with your partners for long-term success",
    "📊 Track which sharing method works best - WhatsApp, Telegram, or social media",
    "🎉 Celebrate every milestone - each referral grows your network!"
  ]);

  useEffect(() => {
    fetchAgentDetails();
  }, []);

  useEffect(() => {
    if (agentDetails) {
      setShowTip(true);
      
      const tipInterval = setInterval(() => {
        setCurrentTipIndex(prev => (prev + 1) % tips.length);
      }, 6000);
  
      const tipVisibilityInterval = setInterval(() => {
        setShowTip(prev => !prev);
      }, 2000);
  
      return () => {
        clearInterval(tipInterval);
        clearInterval(tipVisibilityInterval);
      };
    }
  }, [agentDetails, tips.length]);

  useEffect(() => {
    if (showToast) {
      const timer = setTimeout(() => {
        setShowToast(false);
      }, 3000);
  
      return () => clearTimeout(timer);
    }
  }, [showToast]);

  const fetchAgentDetails = async () => {
    setLoading(true);
    try {
      const response = await axios.get('https://api.globallequityfinance.com/api/referral/agent-details', {
        headers: { 'Authorization': token }
      });
      setAgentDetails(response.data);
      if (response.data) {
        fetchReferralHistory();
        setInviteLink(`https://globallequityfinance.com/signup?invite_code=${response.data.invitationCode}`);
      }
    } catch (error) {
      console.error('Error fetching agent details:', error);
      setAgentDetails(null);
    } finally {
      setLoading(false);
    }
  };

  const fetchReferralHistory = async () => {
    try {
      const response = await axios.get('https://api.globallequityfinance.com/api/referral/invites', {
        headers: { 'Authorization': token }
      });
      
      const validInvites = response.data.filter(invite => 
        invite && invite.invitedUser && invite.invitedUser.fullName
      );
      setReferralHistory(validInvites);
    } catch (error) {
      console.error('Error fetching referral history:', error);
      setToastMessage('Failed to fetch referral data. Please try again.');
      setShowToast(true);
    }
  };

  const applyForReferralProgram = async () => {
    setIsApplying(true);
    try {
      const response = await axios.post('https://api.globallequityfinance.com/api/referral/apply', {}, {
        headers: { 'Authorization': token }
      });
      setAgentDetails(response.data);
      setToastMessage('Successfully joined the investment partners program!');
      setShowToast(true);
      setTimeout(() => fetchReferralHistory(), 1000);
    } catch (error) {
      setToastMessage('Failed to join partners program. Please try again.');
      setShowToast(true);
    } finally {
      setIsApplying(false);
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(inviteLink);
    setToastMessage('Invitation link copied to clipboard!');
    setShowToast(true);
    setCopyButtonText('Copied!');
    
    setTimeout(() => {
      setCopyButtonText('Copy');
    }, 3000);
  };

  const shareViaWhatsApp = () => {
    const message = `🚀 Join me on Global Equity and start investing in company shares! Use my invitation code to get started: ${agentDetails.invitationCode}\n\n${inviteLink}`;
    const encodedMessage = encodeURIComponent(message);
    window.open(`https://wa.me/?text=${encodedMessage}`, '_blank');
  };

  const shareViaTelegram = () => {
    const message = `🚀 Join me on Global Equity and start investing in company shares! Use my invitation code: ${agentDetails.invitationCode}\n\n${inviteLink}`;
    const encodedMessage = encodeURIComponent(message);
    window.open(`https://t.me/share/url?url=${inviteLink}&text=${encodedMessage}`, '_blank');
  };

  const shareViaTwitter = () => {
    const message = `🚀 Join me on Global Equity and start investing in company shares! Use my invitation code: ${agentDetails.invitationCode}`;
    const encodedMessage = encodeURIComponent(message);
    window.open(`https://twitter.com/intent/tweet?text=${encodedMessage}&url=${encodeURIComponent(inviteLink)}`, '_blank');
  };

  const shareViaFacebook = () => {
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(inviteLink)}`, '_blank');
  };

  const toggleSection = (section) => {
    if (expandedSection === section) {
      setExpandedSection(null);
    } else {
      setExpandedSection(section);
    }
  };

  const startOnboarding = () => {
    setShowOnboarding(true);
  };

  const nextStep = () => {
    if (onboardingStep < onboardingSteps.length - 1) {
      setOnboardingStep(prev => prev + 1);
    } else {
      applyForReferralProgram();
      setShowOnboarding(false);
    }
  };

  const contactViaWhatsApp = (mobile) => {
    window.open(`https://wa.me/${mobile}`, '_blank');
  };

  return (
    <div className="team-container">
      {loading ? (
        <Loading message="Loading your partners dashboard..." />
      ) : !agentDetails ? (
        showOnboarding ? (
          <div className="team-onboarding">
            <div className="team-onboarding-step">
              {onboardingSteps[onboardingStep].icon}
              <h2>{onboardingSteps[onboardingStep].title}</h2>
              <p>{onboardingSteps[onboardingStep].description}</p>
              <div className="team-onboarding-progress">
                {onboardingSteps.map((_, index) => (
                  <div 
                    key={index}
                    className={`progress-dot ${index === onboardingStep ? 'active' : ''}`}
                  />
                ))}
              </div>
              <button 
                className="team-next-btn"
                onClick={nextStep}
                disabled={isApplying}
              >
                {onboardingStep === onboardingSteps.length - 1 
                  ? (isApplying ? 'Joining...' : 'Join Now')
                  : 'Continue'
                }
                <FaArrowRight />
              </button>
            </div>
          </div>
        ) : (
          <div className="team-welcome">
            <div className="team-welcome-content">
              <div className="team-welcome-icon">
                <FaBuilding size={64} />
              </div>
              <h1>Investment Partners Program</h1>
              <p className="team-welcome-subtitle">
                Join thousands of partners earning commissions by sharing investment opportunities
              </p>
              
              <div className="team-features-grid">
                <div className="team-feature-card">
                  <div className="team-feature-icon">
                    <MdAttachMoney />
                  </div>
                  <h3>Earn Commissions</h3>
                  <p>Up to 10% on every share investment</p>
                </div>
                
                <div className="team-feature-card">
                  <div className="team-feature-icon">
                    <FaNetworkWired />
                  </div>
                  <h3>Multi-Level Income</h3>
                  <p>Earn from 3 levels of partners</p>
                </div>
                
                <div className="team-feature-card">
                  <div className="team-feature-icon">
                    <IoTrendingUp />
                  </div>
                  <h3>Passive Income</h3>
                  <p>Build sustainable wealth streams</p>
                </div>
                
                <div className="team-feature-card">
                  <div className="team-feature-icon">
                    <FaShieldAlt />
                  </div>
                  <h3>Trusted Platform</h3>
                  <p>Secure & reliable investments</p>
                </div>
              </div>

              <button 
                className="team-cta-button"
                onClick={startOnboarding}
              >
                <FaRocket />
                Get Started
              </button>
            </div>
          </div>
        )
      ) : (
        <>
          <div className="team-header">
            <div className="team-header-content">
              <h1 className="team-title">
                <FaUsers /> Investment Partners
              </h1>
              <p className="team-subtitle">Share opportunities, earn rewards</p>
            </div>
          </div>

          {showTip && (
            <div className="team-tip-banner">
              <FaInfoCircle className="tip-icon" />
              <span>{tips[currentTipIndex]}</span>
            </div>
          )}

          <div className="team-tabs">
            <button 
              className={`team-tab ${activeTab === 'overview' ? 'active' : ''}`}
              onClick={() => setActiveTab('overview')}
            >
              <MdBarChart /> Overview
            </button>
            <button 
              className={`team-tab ${activeTab === 'invite' ? 'active' : ''}`}
              onClick={() => setActiveTab('invite')}
            >
              <FaUserPlus /> Invite
            </button>
            <button 
              className={`team-tab ${activeTab === 'team' ? 'active' : ''}`}
              onClick={() => setActiveTab('team')}
            >
              <FaUserFriends /> My Team
            </button>
          </div>

          <div className="team-content">
            {activeTab === 'overview' && (
              <div className="team-overview-section">
                <div className="team-stats-grid">
                  <div className="team-stat-card">
                    <div className="stat-icon">
                      <FaUsers />
                    </div>
                    <div className="stat-info">
                      <h3>{agentDetails.totalInvites || 0}</h3>
                      <p>Total Partners</p>
                    </div>
                  </div>

                  <div className="team-stat-card">
                    <div className="stat-icon">
                      <FaMoneyBillWave />
                    </div>
                    <div className="stat-info">
                      <h3>KES {(agentDetails.totalCommission || 0).toLocaleString()}</h3>
                      <p>Total Earnings</p>
                    </div>
                  </div>

                  <div className="team-stat-card">
                    <div className="stat-icon">
                      <FaChartLine />
                    </div>
                    <div className="stat-info">
                      <h3>{agentDetails.activeInvites || 0}</h3>
                      <p>Active Investors</p>
                    </div>
                  </div>
                </div>

                <div className="team-commission-card">
                  <div className="commission-header">
                    <h3><FaGift /> Commission Structure</h3>
                  </div>
                  <div className="commission-tiers">
                    <div className="tier-item">
                      <div className="tier-level">Level 1</div>
                      <div className="tier-rate">10%</div>
                      <div className="tier-description">Direct partners' investments</div>
                    </div>
                    <div className="tier-divider">
                      <FaLongArrowAltRight />
                    </div>
                    <div className="tier-item">
                      <div className="tier-level">Level 2</div>
                      <div className="tier-rate">5%</div>
                      <div className="tier-description">Secondary network investments</div>
                    </div>
                    <div className="tier-divider">
                      <FaLongArrowAltRight />
                    </div>
                    <div className="tier-item">
                      <div className="tier-level">Level 3</div>
                      <div className="tier-rate">2%</div>
                      <div className="tier-description">Extended network investments</div>
                    </div>
                  </div>
                </div>

                <div className="team-faq-section">
                  <h3>Frequently Asked Questions</h3>
                  <div className="team-faq-accordion">
                    <div className={`accordion-item ${expandedSection === 'faq1' ? 'expanded' : ''}`}>
                      <div className="accordion-header" onClick={() => toggleSection('faq1')}>
                        <span className="question">How do I earn commissions?</span>
                        <FaChevronDown className="accordion-arrow" />
                      </div>
                      <div className="accordion-content">
                        <p>You earn commissions when users you invite make share investments. Commissions are calculated based on the investment amount and tier level.</p>
                      </div>
                    </div>

                    <div className={`accordion-item ${expandedSection === 'faq2' ? 'expanded' : ''}`}>
                      <div className="accordion-header" onClick={() => toggleSection('faq2')}>
                        <span className="question">When will I receive my commissions?</span>
                        <FaChevronDown className="accordion-arrow" />
                      </div>
                      <div className="accordion-content">
                        <p>Commissions are credited to your account instantly when your partners make successful investments.</p>
                      </div>
                    </div>

                    <div className={`accordion-item ${expandedSection === 'faq3' ? 'expanded' : ''}`}>
                      <div className="accordion-header" onClick={() => toggleSection('faq3')}>
                        <span className="question">Is there a limit to how much I can earn?</span>
                        <FaChevronDown className="accordion-arrow" />
                      </div>
                      <div className="accordion-content">
                        <p>No limits! The more active partners you have, the more you earn. Build your network and watch your passive income grow.</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'invite' && (
              <div className="team-invite-section">
                <div className="invite-card">
                  <div className="invite-header">
                    <FaLink className="invite-icon" />
                    <div>
                      <h3>Your Invitation Code</h3>
                      <p>Share this code with potential investors</p>
                    </div>
                  </div>
                  
                  <div className="invite-code-display">
                    <span className="invite-code">{agentDetails.invitationCode}</span>
                    <button className="copy-btn" onClick={copyToClipboard}>
                      {copyButtonText === 'Copy' ? <FaCopy /> : <FaCheck />}
                      {copyButtonText}
                    </button>
                  </div>

                  <div className="invite-link-section">
                    <label>Share Link</label>
                    <div className="link-display">
                      <input 
                        type="text" 
                        value={inviteLink} 
                        readOnly 
                      />
                    </div>
                  </div>

                  <div className="share-buttons">
                    <h4>Quick Share</h4>
                    <div className="share-options">
                      <button className="share-btn whatsapp" onClick={shareViaWhatsApp}>
                        <FaWhatsapp /> WhatsApp
                      </button>
                      <button className="share-btn telegram" onClick={shareViaTelegram}>
                        <FaTelegram /> Telegram
                      </button>
                      <button className="share-btn twitter" onClick={shareViaTwitter}>
                        <FaTwitter /> Twitter
                      </button>
                      <button className="share-btn facebook" onClick={shareViaFacebook}>
                        <FaFacebook /> Facebook
                      </button>
                    </div>
                  </div>
                </div>

                <div className="tips-card">
                  <h3><FaTrophy /> Pro Tips</h3>
                  <ul>
                    <li>Personalize your invitation messages</li>
                    <li>Share during peak investment hours</li>
                    <li>Follow up with new partners</li>
                    <li>Share success stories</li>
                  </ul>
                </div>
              </div>
            )}

            {activeTab === 'team' && (
              <div className="team-members-section">
                <div className="members-header">
                  <h3>Your Investment Team</h3>
                  <p>{referralHistory.length} active member{referralHistory.length !== 1 ? 's' : ''}</p>
                </div>

                {referralHistory.length === 0 ? (
                  <div className="empty-state">
                    <FaUserFriends size={48} />
                    <h3>No team members yet</h3>
                    <p>Start inviting partners to build your investment network!</p>
                    <button onClick={() => setActiveTab('invite')} className="start-inviting-btn">
                      <FaUserPlus /> Start Inviting
                    </button>
                  </div>
                ) : (
                  <div className="members-list">
                    {referralHistory.map((referral, index) => (
                      <div key={index} className="member-card">
                        <div className="member-avatar">
                          {referral.invitedUser.fullName.charAt(0).toUpperCase()}
                        </div>
                        <div className="member-info">
                          <h4>{referral.invitedUser.fullName}</h4>
                          <p className="member-email">{referral.invitedUser.email}</p>
                          <p className="member-date">
                            Joined {new Date(referral.inviteDate).toLocaleDateString()}
                          </p>
                        </div>
                        <div className="member-actions">
                          <span className="member-commission">
                            +KES {(referral.commission || 0).toLocaleString()}
                          </span>
                          {referral.invitedUser.mobile && (
                            <button 
                              className="contact-btn"
                              onClick={() => contactViaWhatsApp(referral.invitedUser.mobile)}
                            >
                              <FaWhatsapp />
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </>
      )}

      {showToast && (
        <div className="team-toast">
          <FaCheck className="toast-icon" />
          <span className="toast-message">{toastMessage}</span>
        </div>
      )}

      <BottomNavigation />
      <WhatsAppFloat />

      <style jsx>{`
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        :root {
          --primary: #D4AF37;
          --primary-light: #E5C663;
          --primary-dark: #B8962E;
          --secondary: #2C3E50;
          --accent: #3498DB;
          --bg-dark: #0F1419;
          --card-bg: #1A1F2E;
          --surface-bg: #151922;
          --border-color: rgba(255, 255, 255, 0.1);
          --text-primary: #FFFFFF;
          --text-secondary: #B0B8C1;
          --success: #27AE60;
          --error: #E74C3C;
          --shadow-soft: 0 2px 8px rgba(0, 0, 0, 0.2);
          --shadow-strong: 0 8px 24px rgba(0, 0, 0, 0.4);
          --animation-speed: 0.3s;
        }

        .team-container {
          min-height: 100vh;
          background: var(--bg-dark);
          padding-bottom: 80px;
        }

        .team-header {
          background: linear-gradient(135deg, var(--card-bg) 0%, var(--surface-bg) 100%);
          padding: 24px 20px;
          border-bottom: 1px solid var(--border-color);
        }

        .team-header-content {
          max-width: 1200px;
          margin: 0 auto;
        }

        .team-title {
          font-size: 28px;
          font-weight: 700;
          color: var(--text-primary);
          margin-bottom: 8px;
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .team-subtitle {
          font-size: 15px;
          color: var(--text-secondary);
        }

        .team-tip-banner {
          background: linear-gradient(90deg, rgba(212, 175, 55, 0.15) 0%, rgba(212, 175, 55, 0.05) 100%);
          border: 1px solid rgba(212, 175, 55, 0.3);
          border-radius: 12px;
          padding: 14px 16px;
          margin: 16px 20px;
          display: flex;
          align-items: center;
          gap: 12px;
          animation: slideIn 0.5s ease;
        }

        @keyframes slideIn {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .tip-icon {
          color: var(--primary);
          font-size: 20px;
          flex-shrink: 0;
        }

        .team-tip-banner span {
          color: var(--text-primary);
          font-size: 14px;
          line-height: 1.5;
        }

        .team-tabs {
          display: flex;
          gap: 8px;
          padding: 16px 20px;
          overflow-x: auto;
          border-bottom: 1px solid var(--border-color);
        }

        .team-tab {
          background: var(--surface-bg);
          border: 1px solid var(--border-color);
          border-radius: 10px;
          padding: 10px 18px;
          color: var(--text-secondary);
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 8px;
          transition: all var(--animation-speed) ease;
          white-space: nowrap;
        }

        .team-tab:hover {
          background: rgba(212, 175, 55, 0.1);
          border-color: var(--primary);
        }

        .team-tab.active {
          background: var(--primary);
          color: white;
          border-color: var(--primary);
        }

        .team-content {
          padding: 20px;
          max-width: 1200px;
          margin: 0 auto;
        }

        .team-overview-section {
          display: flex;
          flex-direction: column;
          gap: 20px;
        }

        .team-stats-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
          gap: 14px;
        }

        .team-stat-card {
          background: var(--card-bg);
          border: 1px solid var(--border-color);
          border-radius: 14px;
          padding: 18px;
          display: flex;
          align-items: center;
          gap: 14px;
          transition: transform var(--animation-speed) ease;
        }

        .team-stat-card:hover {
          transform: translateY(-4px);
        }

        .stat-icon {
          width: 48px;
          height: 48px;
          border-radius: 12px;
          background: rgba(212, 175, 55, 0.15);
          display: flex;
          align-items: center;
          justify-content: center;
          color: var(--primary);
          font-size: 22px;
        }

        .stat-info h3 {
          font-size: 24px;
          font-weight: 700;
          color: var(--text-primary);
          margin-bottom: 4px;
        }

        .stat-info p {
          font-size: 13px;
          color: var(--text-secondary);
        }

        .team-commission-card {
          background: var(--card-bg);
          border: 1px solid var(--border-color);
          border-radius: 16px;
          padding: 20px;
        }

        .commission-header {
          margin-bottom: 20px;
        }

        .commission-header h3 {
          font-size: 18px;
          font-weight: 600;
          color: var(--text-primary);
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .commission-tiers {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          flex-wrap: wrap;
        }

        .tier-item {
          flex: 1;
          min-width: 120px;
          background: var(--surface-bg);
          border: 1px solid var(--border-color);
          border-radius: 12px;
          padding: 16px;
          text-align: center;
        }

        .tier-level {
          font-size: 13px;
          color: var(--text-secondary);
          margin-bottom: 8px;
        }

        .tier-rate {
          font-size: 28px;
          font-weight: 700;
          color: var(--primary);
          margin-bottom: 8px;
        }

        .tier-description {
          font-size: 12px;
          color: var(--text-secondary);
          line-height: 1.4;
        }

        .tier-divider {
          color: var(--text-secondary);
          font-size: 20px;
        }

        .team-faq-section {
          background: var(--card-bg);
          border: 1px solid var(--border-color);
          border-radius: 16px;
          padding: 20px;
        }

        .team-faq-section h3 {
          font-size: 18px;
          font-weight: 600;
          color: var(--text-primary);
          margin-bottom: 16px;
        }

        .team-faq-accordion {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .accordion-item {
          background: var(--surface-bg);
          border: 1px solid var(--border-color);
          border-radius: 12px;
          overflow: hidden;
        }

        .accordion-header {
          padding: 16px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          cursor: pointer;
          transition: background var(--animation-speed) ease;
        }

        .accordion-header:hover {
          background: rgba(255, 255, 255, 0.03);
        }

        .question {
          font-size: 15px;
          font-weight: 500;
          color: var(--text-primary);
        }

        .accordion-arrow {
          color: var(--text-secondary);
          transition: transform var(--animation-speed) ease;
          font-size: 14px;
        }

        .accordion-item.expanded .accordion-arrow {
          transform: rotate(180deg);
        }

        .accordion-content {
          max-height: 0;
          overflow: hidden;
          transition: max-height var(--animation-speed) ease;
        }

        .accordion-item.expanded .accordion-content {
          max-height: 200px;
        }

        .accordion-content p {
          padding: 0 16px 16px;
          font-size: 14px;
          color: var(--text-secondary);
          line-height: 1.6;
        }

        .team-invite-section {
          display: flex;
          flex-direction: column;
          gap: 20px;
        }

        .invite-card {
          background: var(--card-bg);
          border: 1px solid var(--border-color);
          border-radius: 16px;
          padding: 24px;
        }

        .invite-header {
          display: flex;
          align-items: center;
          gap: 16px;
          margin-bottom: 24px;
        }

        .invite-icon {
          font-size: 32px;
          color: var(--primary);
        }

        .invite-header h3 {
          font-size: 18px;
          font-weight: 600;
          color: var(--text-primary);
          margin-bottom: 4px;
        }

        .invite-header p {
          font-size: 13px;
          color: var(--text-secondary);
        }

        .invite-code-display {
          background: var(--surface-bg);
          border: 1px solid var(--border-color);
          border-radius: 12px;
          padding: 16px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
        }

        .invite-code {
          font-size: 24px;
          font-weight: 700;
          color: var(--primary);
          letter-spacing: 2px;
        }

        .copy-btn {
          background: var(--primary);
          color: white;
          border: none;
          border-radius: 10px;
          padding: 10px 16px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 8px;
          transition: all var(--animation-speed) ease;
        }

        .copy-btn:hover {
          background: var(--primary-light);
          transform: translateY(-2px);
        }

        .invite-link-section {
          margin-bottom: 24px;
        }

        .invite-link-section label {
          display: block;
          font-size: 14px;
          font-weight: 500;
          color: var(--text-primary);
          margin-bottom: 10px;
        }

        .link-display input {
          width: 100%;
          background: var(--surface-bg);
          border: 1px solid var(--border-color);
          border-radius: 10px;
          padding: 12px;
          color: var(--text-primary);
          font-size: 13px;
        }

        .share-buttons h4 {
          font-size: 15px;
          font-weight: 600;
          color: var(--text-primary);
          margin-bottom: 14px;
        }

        .share-options {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
          gap: 10px;
        }

        .share-btn {
          background: var(--surface-bg);
          border: 1px solid var(--border-color);
          border-radius: 10px;
          padding: 12px;
          color: var(--text-primary);
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          transition: all var(--animation-speed) ease;
        }

        .share-btn:hover {
          transform: translateY(-2px);
        }

        .share-btn.whatsapp:hover {
          background: #25D366;
          color: white;
          border-color: #25D366;
        }

        .share-btn.telegram:hover {
          background: #0088cc;
          color: white;
          border-color: #0088cc;
        }

        .share-btn.twitter:hover {
          background: #1DA1F2;
          color: white;
          border-color: #1DA1F2;
        }

        .share-btn.facebook:hover {
          background: #1877F2;
          color: white;
          border-color: #1877F2;
        }

        .tips-card {
          background: var(--card-bg);
          border: 1px solid var(--border-color);
          border-radius: 16px;
          padding: 20px;
        }

        .tips-card h3 {
          font-size: 18px;
          font-weight: 600;
          color: var(--text-primary);
          margin-bottom: 16px;
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .tips-card ul {
          list-style: none;
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .tips-card li {
          font-size: 14px;
          color: var(--text-secondary);
          padding-left: 24px;
          position: relative;
          line-height: 1.6;
        }

        .tips-card li::before {
          content: '✓';
          position: absolute;
          left: 0;
          color: var(--primary);
          font-weight: bold;
        }

        .team-members-section {
          background: var(--card-bg);
          border: 1px solid var(--border-color);
          border-radius: 16px;
          padding: 20px;
        }

        .members-header {
          margin-bottom: 20px;
        }

        .members-header h3 {
          font-size: 18px;
          font-weight: 600;
          color: var(--text-primary);
          margin-bottom: 6px;
        }

        .members-header p {
          font-size: 13px;
          color: var(--text-secondary);
        }

        .empty-state {
          text-align: center;
          padding: 40px 20px;
        }

        .empty-state svg {
          color: var(--text-secondary);
          opacity: 0.5;
          margin-bottom: 16px;
        }

        .empty-state h3 {
          font-size: 18px;
          font-weight: 600;
          color: var(--text-primary);
          margin-bottom: 8px;
        }

        .empty-state p {
          font-size: 14px;
          color: var(--text-secondary);
          margin-bottom: 20px;
        }

        .start-inviting-btn {
          background: var(--primary);
          color: white;
          border: none;
          border-radius: 10px;
          padding: 12px 24px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          gap: 8px;
          transition: all var(--animation-speed) ease;
        }

        .start-inviting-btn:hover {
          background: var(--primary-light);
          transform: translateY(-2px);
        }

        .members-list {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .member-card {
          background: var(--surface-bg);
          border: 1px solid var(--border-color);
          border-radius: 12px;
          padding: 16px;
          display: flex;
          align-items: center;
          gap: 14px;
          transition: transform var(--animation-speed) ease;
        }

        .member-card:hover {
          transform: translateX(4px);
        }

        .member-avatar {
          width: 48px;
          height: 48px;
          border-radius: 12px;
          background: linear-gradient(135deg, var(--primary) 0%, var(--primary-light) 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 20px;
          font-weight: 700;
          color: white;
          flex-shrink: 0;
        }

        .member-info {
          flex: 1;
        }

        .member-info h4 {
          font-size: 15px;
          font-weight: 600;
          color: var(--text-primary);
          margin-bottom: 4px;
        }

        .member-email {
          font-size: 12px;
          color: var(--text-secondary);
          margin-bottom: 4px;
        }

        .member-date {
          font-size: 11px;
          color: var(--text-secondary);
        }

        .member-actions {
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .member-commission {
          background: rgba(39, 174, 96, 0.15);
          color: var(--success);
          border-radius: 8px;
          padding: 6px 12px;
          font-size: 13px;
          font-weight: 600;
        }

        .contact-btn {
          width: 36px;
          height: 36px;
          border-radius: 10px;
          background: #25D366;
          color: white;
          border: none;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all var(--animation-speed) ease;
          font-size: 18px;
        }

        .contact-btn:hover {
          transform: scale(1.1);
        }

        .team-welcome {
          min-height: calc(100vh - 80px);
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 20px;
        }

        .team-welcome-content {
          max-width: 900px;
          text-align: center;
        }

        .team-welcome-icon {
          width: 100px;
          height: 100px;
          border-radius: 20px;
          background: linear-gradient(135deg, rgba(212, 175, 55, 0.2) 0%, rgba(212, 175, 55, 0.05) 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 24px;
          color: var(--primary);
        }

        .team-welcome-content h1 {
          font-size: 32px;
          font-weight: 700;
          color: var(--text-primary);
          margin-bottom: 12px;
        }

        .team-welcome-subtitle {
          font-size: 16px;
          color: var(--text-secondary);
          margin-bottom: 40px;
          line-height: 1.6;
        }

        .team-features-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
          gap: 16px;
          margin-bottom: 40px;
        }

        .team-feature-card {
          background: var(--card-bg);
          border: 1px solid var(--border-color);
          border-radius: 16px;
          padding: 24px;
          transition: all var(--animation-speed) ease;
        }

        .team-feature-card:hover {
          transform: translateY(-6px);
          border-color: var(--primary);
        }

        .team-feature-icon {
          width: 56px;
          height: 56px;
          border-radius: 14px;
          background: rgba(212, 175, 55, 0.15);
          display: flex;
          align-items: center;
          justify-content: center;
          color: var(--primary);
          font-size: 26px;
          margin: 0 auto 16px;
        }

        .team-feature-card h3 {
          font-size: 16px;
          font-weight: 600;
          color: var(--text-primary);
          margin-bottom: 8px;
        }

        .team-feature-card p {
          font-size: 13px;
          color: var(--text-secondary);
          line-height: 1.5;
        }

        .team-cta-button {
          background: var(--primary);
          color: white;
          border: none;
          border-radius: 12px;
          padding: 16px 32px;
          font-size: 16px;
          font-weight: 600;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          gap: 10px;
          transition: all var(--animation-speed) ease;
        }

        .team-cta-button:hover {
          background: var(--primary-light);
          transform: translateY(-2px);
          box-shadow: 0 8px 20px rgba(212, 175, 55, 0.3);
        }

        .team-onboarding {
          min-height: calc(100vh - 80px);
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 20px;
        }

        .team-onboarding-step {
          max-width: 500px;
          background: var(--card-bg);
          border: 1px solid var(--border-color);
          border-radius: 20px;
          padding: 40px 30px;
          text-align: center;
        }

        .team-onboarding-step svg {
          margin-bottom: 24px;
        }

        .team-onboarding-step h2 {
          font-size: 24px;
          font-weight: 700;
          color: var(--text-primary);
          margin-bottom: 16px;
        }

        .team-onboarding-step p {
          font-size: 15px;
          color: var(--text-secondary);
          line-height: 1.7;
          margin-bottom: 32px;
        }

        .team-onboarding-progress {
          display: flex;
          justify-content: center;
          gap: 10px;
          margin-bottom: 32px;
        }

        .progress-dot {
          width: 10px;
          height: 10px;
          border-radius: 50%;
          background: var(--border-color);
          transition: all var(--animation-speed) ease;
        }

        .progress-dot.active {
          background: var(--primary);
          width: 30px;
          border-radius: 5px;
        }

        .team-next-btn {
          background: var(--primary);
          color: white;
          border: none;
          border-radius: 12px;
          padding: 14px 28px;
          font-size: 15px;
          font-weight: 600;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          gap: 10px;
          transition: all var(--animation-speed) ease;
        }

        .team-next-btn:hover:not(:disabled) {
          background: var(--primary-light);
          transform: translateY(-2px);
        }

        .team-next-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .team-toast {
          position: fixed;
          bottom: 100px;
          left: 50%;
          transform: translateX(-50%);
          background: var(--card-bg);
          border: 1px solid var(--border-color);
          border-radius: 12px;
          padding: 14px 18px;
          display: flex;
          align-items: center;
          gap: 12px;
          box-shadow: var(--shadow-strong);
          z-index: 1000;
          animation: toastFadeIn 0.3s ease;
          max-width: 90%;
        }

        @keyframes toastFadeIn {
          from { opacity: 0; transform: translate(-50%, 20px); }
          to { opacity: 1; transform: translate(-50%, 0); }
        }

        .toast-icon {
          color: var(--success);
          font-size: 18px;
        }

        .toast-message {
          color: var(--text-primary);
          font-size: 14px;
        }

        @media (max-width: 768px) {
          .team-title {
            font-size: 24px;
          }

          .team-stats-grid {
            grid-template-columns: 1fr;
          }

          .commission-tiers {
            flex-direction: column;
          }

          .tier-divider {
            transform: rotate(90deg);
          }

          .share-options {
            grid-template-columns: repeat(2, 1fr);
          }

          .team-features-grid {
            grid-template-columns: 1fr;
          }

          .team-welcome-content h1 {
            font-size: 26px;
          }
        }

        @media (max-width: 480px) {
          .team-header {
            padding: 20px 16px;
          }

          .team-content {
            padding: 16px;
          }

          .team-stat-card {
            padding: 14px;
          }

          .stat-icon {
            width: 40px;
            height: 40px;
            font-size: 18px;
          }

          .stat-info h3 {
            font-size: 20px;
          }

          .invite-code {
            font-size: 20px;
          }

          .member-card {
            flex-direction: column;
            align-items: flex-start;
            gap: 12px;
          }

          .member-actions {
            width: 100%;
            justify-content: space-between;
            padding-top: 12px;
            border-top: 1px solid var(--border-color);
          }
        }
      `}</style>
    </div>
  );
};

export default AffiliateProgram;
